import { annotationTypography, hasModernAnnotationStyle } from '../../annotations/typography';
import { Map as MapLibreMap, type MapOptions, type StyleSpecification } from 'maplibre-gl';

import type { SourceDescriptor } from '../../data/model';
import { createMapLibreRenderPlan } from '../../map/mapLibreRenderPlan';
import { loadMapLibreLabelFonts } from '../../map/mapLibreLabelFonts';
import { ensureMapLibreWorkerConfigured } from '../../map/mapLibreWorker';
import type { LayerDefinition } from '../../project/model';
import type { StaticCompositionRenderPlan } from './planning';
import { StaticExportError } from './model';
import {
  assertStaticMapFrameSupported,
  getStaticWebGlCapabilities,
  type StaticWebGlCapabilities,
} from './webGlCapabilities';
import { DEFAULT_BASEMAP_CONFIGURATION, type BasemapConfiguration } from '../../basemaps/catalog';
import {
  composeBasemapStyle,
  firstProviderLabelLayerId,
  resolveMapLibreBasemapStyle,
  splitDropMapLayersForProviderLabels,
} from '../../basemaps/mapLibre';
import { OperationCancelledError, throwIfOperationCancelled } from '../../operations/longOperation';
import type { AnnotationLayer } from '../../annotations/model';
import { mapAnnotationProjection } from '../../annotations/elevation';
import { compositionAnnotationRenderScale } from '../scale';
import { singleWorldMinimumZoom } from '../../map/worldViewport';
import { SymbolImageCache, type PreparedSymbolCatalogue } from '../../symbols/images';
import type { SymbolAsset } from '../../symbols/model';
import { sceneSymbolRequests } from '../../symbols/scene';

export const STATIC_MAP_RENDER_TIMEOUT_MS = 20_000;
export const STATIC_MAP_CONTAINER_CLASS = 'dropmap-static-export-map';

type StaticMapConsumer<T> = (
  canvas: HTMLCanvasElement,
  map: MapLibreMap,
  release: () => void,
) => T | Promise<T>;

export interface StaticMapRendererDependencies {
  readonly symbolAssets?: readonly SymbolAsset[];
  readonly symbolImages?: PreparedSymbolCatalogue;
  readonly renderWorldCopies?: boolean;
  readonly presentationScale?: number;
  readonly mainZoom?: number;
  readonly sceneMode?: 'complete' | 'projection' | 'basemap-only' | 'labels-only';
  /** Configured provider style; never contains Placeio Maps source/layer additions. */
  readonly basemapStyle?: StyleSpecification;
  readonly basemapConfiguration?: BasemapConfiguration;
  readonly createMap?: (options: MapOptions) => MapLibreMap;
  readonly timeoutMs?: number;
  readonly webGlCapabilities?: StaticWebGlCapabilities | null;
  readonly signal?: AbortSignal;
  readonly annotationLayers?: readonly AnnotationLayer[];
  readonly activeFocusAreaId?: string | null;
}

function createExportContainer(width: number, height: number): HTMLDivElement {
  const container = document.createElement('div');
  container.className = STATIC_MAP_CONTAINER_CLASS;
  container.setAttribute('aria-hidden', 'true');
  Object.assign(container.style, {
    position: 'fixed',
    left: '-100000px',
    top: '0',
    width: `${width}px`,
    height: `${height}px`,
    overflow: 'hidden',
    pointerEvents: 'none',
  });
  document.body.append(container);
  return container;
}

function waitForEvent(
  map: MapLibreMap,
  event: 'idle' | 'load',
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new OperationCancelledError());
      return;
    }
    const lost = () => {
      window.clearTimeout(timeout);
      map.off(event, ready);
      map.off('webglcontextlost', lost);
      signal?.removeEventListener('abort', cancelled);
      reject(
        new StaticExportError(
          'webgl-limit',
          'The graphics device lost the export rendering context. The project is unchanged; try exporting again.',
        ),
      );
    };
    const cancelled = () => {
      window.clearTimeout(timeout);
      map.off(event, ready);
      map.off('webglcontextlost', lost);
      reject(new OperationCancelledError());
    };
    const timeout = window.setTimeout(() => {
      signal?.removeEventListener('abort', cancelled);
      map.off(event, ready);
      map.off('webglcontextlost', lost);
      reject(
        new StaticExportError('map-timeout', 'Could not finish rendering the map for export.'),
      );
    }, timeoutMs);
    const ready = () => {
      window.clearTimeout(timeout);
      signal?.removeEventListener('abort', cancelled);
      map.off(event, ready);
      map.off('webglcontextlost', lost);
      resolve();
    };
    signal?.addEventListener('abort', cancelled, { once: true });
    map.on(event, ready);
    map.on('webglcontextlost', lost);
  });
}

async function ensureMapLabelFonts(
  layers: readonly LayerDefinition[],
  annotationLayers: readonly AnnotationLayer[] = [],
): Promise<void> {
  const loading = loadMapLibreLabelFonts(
    [
      ...layers.filter((layer) => layer.labels.enabled).map((layer) => layer.labels),
      ...annotationLayers.flatMap((layer) =>
        layer.annotations.flatMap((annotation) =>
          annotation.type === 'text' || annotation.type === 'callout'
            ? [
                {
                  ...annotationTypography(annotation.style),
                },
              ]
            : [],
        ),
      ),
    ],
    annotationLayers.some((layer) =>
      layer.annotations.some(
        (annotation) =>
          (annotation.type === 'text' || annotation.type === 'callout') &&
          hasModernAnnotationStyle(annotation.style),
      ),
    ),
  );
  if (loading) await loading;
}

/**
 * Renders the authored map scene through an isolated MapLibre instance. The consumer runs while
 * its drawing buffer is valid; the map and hidden container are then always destroyed.
 */
export async function withRenderedStaticMapFrame<T>(
  plan: StaticCompositionRenderPlan,
  backgroundColour: string,
  sources: readonly SourceDescriptor[],
  layers: readonly LayerDefinition[],
  consume: StaticMapConsumer<T>,
  dependencies: StaticMapRendererDependencies = {},
): Promise<T> {
  const { widthPx: width, heightPx: height } = plan.mapFrame.logicalViewport;
  const capabilities =
    dependencies.webGlCapabilities === undefined
      ? getStaticWebGlCapabilities()
      : dependencies.webGlCapabilities;
  assertStaticMapFrameSupported(plan, capabilities);
  const container = createExportContainer(width, height);
  const symbolCache = new SymbolImageCache();
  let map: MapLibreMap | null = null;
  let mapRenderingComplete = false;
  let contextLost = false;
  const recordContextLoss = () => {
    contextLost = true;
  };
  const release = () => {
    if (map) {
      const canvas = map.getCanvas();
      map.off('webglcontextlost', recordContextLoss);
      map.remove();
      canvas.width = 1;
      canvas.height = 1;
      map = null;
    }
    container.remove();
  };
  try {
    throwIfOperationCancelled(dependencies.signal);
    const mode = dependencies.sceneMode ?? 'complete';
    const symbolImages =
      dependencies.symbolImages ??
      (await symbolCache.graphics(
        mode === 'complete' || mode === 'labels-only'
          ? sceneSymbolRequests(layers, dependencies.annotationLayers)
          : [],
        dependencies.symbolAssets ?? [],
        Math.max(2, plan.mapFrame.pixelRatio),
        dependencies.signal,
      ));
    if (mode === 'complete' || mode === 'labels-only')
      await ensureMapLabelFonts(layers, dependencies.annotationLayers);
    const basemapConfiguration = dependencies.basemapConfiguration ?? DEFAULT_BASEMAP_CONFIGURATION;
    const style =
      mode === 'projection' || (mode === 'labels-only' && basemapConfiguration.presetId === 'none')
        ? ({ version: 8, sources: {}, layers: [] } satisfies StyleSpecification)
        : dependencies.basemapStyle
          ? composeBasemapStyle(dependencies.basemapStyle, basemapConfiguration, backgroundColour)
          : await resolveMapLibreBasemapStyle(basemapConfiguration, backgroundColour);
    if (mode === 'basemap-only' || mode === 'labels-only') {
      // Match the live geometry-below-provider-text order across the two hybrid raster passes.
      // Keep glyphs, sprites, sources and symbol metrics; only suppress the other pass's paint.
      for (const layer of style.layers) {
        const text = layer.type === 'symbol' && layer.layout?.['text-field'] !== undefined;
        if (mode === 'basemap-only' ? text : !text)
          layer.layout = { ...layer.layout, visibility: 'none' };
      }
    }
    ensureMapLibreWorkerConfigured();
    const createMap = dependencies.createMap ?? ((options: MapOptions) => new MapLibreMap(options));
    map = createMap({
      container,
      style,
      center: [...plan.mapFrame.view.center],
      zoom:
        (dependencies.renderWorldCopies ?? true)
          ? Math.max(
              plan.mapFrame.view.zoom,
              singleWorldMinimumZoom(
                width,
                height,
                plan.mapFrame.view.bearing,
                plan.mapFrame.view.pitch,
              ),
            )
          : plan.mapFrame.view.zoom,
      bearing: plan.mapFrame.view.bearing,
      pitch: plan.mapFrame.view.pitch,
      minZoom: -10,
      maxZoom: 24,
      attributionControl: false,
      interactive: false,
      renderWorldCopies: dependencies.renderWorldCopies ?? true,
      fadeDuration: 0,
      pixelRatio: plan.mapFrame.pixelRatio,
      maxCanvasSize: [capabilities.maxMapWidth, capabilities.maxMapHeight],
      // At high backing densities the existing pixels supply edge coverage without an MSAA buffer.
      canvasContextAttributes: {
        preserveDrawingBuffer: true,
        antialias: mode !== 'projection' && plan.mapFrame.pixelRatio < 2,
      },
    });

    map.on('webglcontextlost', recordContextLoss);
    await waitForEvent(
      map,
      'load',
      dependencies.timeoutMs ?? STATIC_MAP_RENDER_TIMEOUT_MS,
      dependencies.signal,
    );
    const renderPlan = createMapLibreRenderPlan(
      {
        sources,
        symbolImages,
        presentationScale: dependencies.presentationScale ?? 1,
        layers: mode === 'basemap-only' || mode === 'projection' ? [] : layers,
        annotationLayers:
          mode === 'basemap-only' || mode === 'projection' || mode === 'labels-only'
            ? []
            : (dependencies.annotationLayers ?? []),
        activeFocusAreaId: dependencies.activeFocusAreaId ?? null,
        annotationRenderScale: compositionAnnotationRenderScale(plan.mapFrame.logicalPixelsPerMm),
      },
      typeof map.getVerticalFieldOfView === 'function'
        ? mapAnnotationProjection(map)
        : {
            project: (coordinate) => {
              const point = map!.project([...coordinate]);
              return { x: point.x, y: point.y };
            },
            unproject: (point) => {
              const coordinate = map!.unproject([point.x, point.y]);
              return [coordinate.lng, coordinate.lat];
            },
          },
    );
    for (const source of renderPlan.sources) map.addSource(source.id, source.definition);
    for (const image of mode === 'labels-only' ? [] : renderPlan.images) {
      map.addImage(
        image.id,
        { width: image.width, height: image.height, data: image.data },
        {
          pixelRatio: image.pixelRatio,
          ...(image.sdf === undefined ? {} : { sdf: image.sdf }),
          ...(image.stretchX === undefined ? {} : { stretchX: image.stretchX }),
          ...(image.stretchY === undefined ? {} : { stretchY: image.stretchY }),
          ...(image.content === undefined ? {} : { content: image.content }),
        },
      );
    }
    const split = splitDropMapLayersForProviderLabels(renderPlan.layers);
    const providerLabelLayerId = firstProviderLabelLayerId(style);
    for (const layer of [...split.focus, ...split.geometry]) {
      if (mode !== 'labels-only') {
        if (providerLabelLayerId) map.addLayer(layer, providerLabelLayerId);
        else map.addLayer(layer);
      }
    }
    for (const layer of split.labels) {
      map.addLayer(layer);
    }
    if (mode !== 'labels-only') {
      for (const layer of split.dataFocus) map.addLayer(layer);
      for (const layer of split.annotations) map.addLayer(layer);
    }
    map.triggerRepaint();
    await waitForEvent(
      map,
      'idle',
      dependencies.timeoutMs ?? STATIC_MAP_RENDER_TIMEOUT_MS,
      dependencies.signal,
    );

    const canvas = map.getCanvas();
    if (
      canvas.width !== plan.mapFrame.backingSize.widthPx ||
      canvas.height !== plan.mapFrame.backingSize.heightPx
    ) {
      throw new StaticExportError(
        'webgl-limit',
        `${plan.dpi} DPI could not be created for this map frame on this device. Choose a lower resolution.`,
      );
    }
    mapRenderingComplete = true;
    throwIfOperationCancelled(dependencies.signal);
    const result = await consume(canvas, map, release);
    if (contextLost)
      throw new StaticExportError(
        'webgl-limit',
        'The graphics device lost the export rendering context. The project is unchanged; try exporting again.',
      );
    return result;
  } catch (cause) {
    throwIfOperationCancelled(dependencies.signal);
    if (cause instanceof StaticExportError || cause instanceof OperationCancelledError) throw cause;
    // Consumer errors belong to their own export phase; only setup/render failures are MapLibre failures.
    if (mapRenderingComplete) throw cause;
    throw new StaticExportError(
      'map-initialisation',
      'Could not finish rendering the map for export.',
      { cause },
    );
  } finally {
    symbolCache.clear();
    release();
  }
}
